import React, { FC, useEffect, useReducer, useState } from 'react';
import { AwardStatusEnum, AwardTypeEnum, EnrolmentType, WalletAwardFormMode } from '../../../../types/award-type';
import { useCommonStyles } from '../../../../../../common/styles/common-styles';
import {
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { WALLET_BASE_PATH, WALLET_AWARD_PATH } from '../../../../constants';
import { useIntl } from 'react-intl';
import { ErrorFieldType, useErrorHandler, fileUpload, getLocalDateFormat } from 'src/app/common/utils';
import { useDispatch } from 'react-redux';
import { useStyles } from './AwardDetail.style';
import {
  initialState,
  walletAwardFormReducer,
  detailToStateConvertor,
  AwardDetailProps,
  getSubmitDetail,
  WalletAwardFormState,
  FileItem,
  isStrictlyNegative,
  EDITABLE_FIELDS,
} from './AwardDetail.util';
import { AlertType, appendAlertItem, MANDATORY_FIELD_ERROR_TEXT } from '@pruforce/common-adminweb-sdk';
import { createBlob, getBlob } from 'src/app/common/network';
import Tinymce from './Tinymce';
import MultiFileUploader from './MultiFileUploader';
import { PruDatePicker } from 'src/app/common/components/PruDatePicker';
import { TargetApplicantComponent } from './target-applicant/target-applicant.component';
import { createWalletAward, modifyWalletAward } from 'src/app/modules/pruwallet/network/awardCrud';
import { useWalletAwardPermission } from '../../hook/useWalletAwardPermission';

const AwardDetail: FC<AwardDetailProps> = ({ detail, code, formMode, onReload, onRouteTo }) => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const [uploadThumbnailProgress, setUploadThumbnailProgress] = useState<number>();
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);

  const [formState, formDispatch] = useReducer(
    walletAwardFormReducer,
    detail ? detailToStateConvertor(detail) : initialState,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submittingAction, setSubmittingAction] = useState<string>('');
  const isDisabled = formMode === WalletAwardFormMode.VIEW;
  const isEdit = formMode === WalletAwardFormMode.EDIT;

  // Get permission
  const { approvePermission } = useWalletAwardPermission();

  const isEditableField = (filed: string): boolean => {
    if (isDisabled) {
      return false;
    }
    //Draft all fields can editable
    if (formState.status === AwardStatusEnum.DRAFT) {
      return true;
    }
    // Active / Paused: only fields in editableFields can be edited and must have approve permission
    if (
      approvePermission &&
      (formState.status === AwardStatusEnum.ACTIVE || formState.status === AwardStatusEnum.PAUSED)
    ) {
      return EDITABLE_FIELDS.includes(filed);
    }

    return false;
  };

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    {
      name: 'awardId',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'awardNameZh',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'awardNameEn',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'awardType',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'designation',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (
          // by desigation
          formState.enrolmentType === EnrolmentType.BY_SEGMENT
        ) {
          if (
            (!formState.eligibleDesignationsHK || formState.eligibleDesignationsHK?.length == 0) &&
            (!formState.eligibleDesignationsMO || formState.eligibleDesignationsMO?.length == 0)
          )
            return true;
        } else if (
          // by upload agent list
          formState.enrolmentType === EnrolmentType.SPECIFIC &&
          (!formState.agentListUploaded || formState.agentListUploaded?.length === 0)
        ) {
          return true;
        }
        return false;
      },
    },
    {
      name: 'thumbnailFileName',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        return !(formState.thumbnail && formState.thumbnail.url);
      },
    },
    {
      name: 'awardDetailsZh',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'awardDetailsEn',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'requireRedemption',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'minCreditForRedemption',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (!formState.requireRedemption) {
          return false;
        }
        return formState.minCreditForRedemption ? false : true;
      },
    },
    {
      name: 'creditDeductedAtRedemption',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (!formState.requireRedemption) {
          return false;
        }
        return formState.creditDeductedAtRedemption ? false : true;
      },
    },
    {
      name: 'awardValidityStartDate',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'awardValidityEndDate',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'promoCodeUsageLimit',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (formState.awardType === AwardTypeEnum.PROMO_CODE) {
          return formState.promoCodeUsageLimit ? false : true;
        }
        return false;
      },
    },
    {
      name: 'maximumNegativeCap',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (!formState.creditDeductedAtSubmission || !formState.requireRedemption || !formState.allowNegativeBalance) {
          return false;
        }
        return isStrictlyNegative(formState.maximumNegativeCap) ? false : true;
      },
    },
    {
      name: 'creditDeductionValue',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        if (!formState.creditDeductedAtSubmission) {
          return false;
        }
        return formState.creditDeductionValue ? false : true;
      },
    },
  ]);

  useEffect(() => {
    onDismissErrorHandler('designation', true);
  }, [formState.enrolmentType]);

  const initThumbnail = async () => {
    if (!detail?.thumbnailUrl) {
      return;
    }
    const url = detail?.thumbnailUrl;
    const blobId = url.startsWith('res://') ? url.replace('res://', '') : url;
    if (!blobId) {
      return;
    }
    getBlob({ resourceIds: blobId }, dispatch)
      .then((resources) => {
        if (resources && resources[0] && resources[0].url) {
          formDispatch({
            type: 'UPLOAD_THUMBNAIL_IMAGE',
            payload: {
              blobId: blobId,
              name: resources[0].blobName,
              url: resources[0].url,
            },
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const initRelatedFlyers = async () => {
    if (!detail?.files) {
      return;
    }
    Promise.all(
      detail?.files
        .filter((m) => m?.fileUrl && m?.fileUrl.startsWith('res://'))
        .map(async (m: FileItem) => {
          const blobId = m?.fileUrl?.replace('res://', '');
          if (blobId) {
            const resources = await getBlob({ resourceIds: blobId }, dispatch);
            if (resources && resources[0]) {
              return { blobId: blobId, fileName: m.fileName, url: resources[0].url };
            }
          }
          return {};
        }),
    )
      .then((results) => {
        const list = results.filter((m) => !!m.url);
        if (list) {
          formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'relatedFlyers', value: list } });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    if (code) {
      initThumbnail();
      initRelatedFlyers();
    }
  }, [code]);

  const getImageSizeFromUrl = (url: string): Promise<{ width: number; height: number } | null> => {
    return new Promise((res) => {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        res({
          width: img.width,
          height: img.height,
        });
      };

      img.onerror = () => {
        res(null);
      };
    });
  };
  const TranslationWithVariable = (key: string, count: number | string) =>
    intl.formatMessage({ id: key }, { num: count });

  // eslint-disable-next-line
  const handleThumbnailFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const fileSize = file.size / 1024;
      const errMsg = TranslationWithVariable('global.max.file.size.allow', '') || '';
      e.target.value = '';
      //TODO: check the file size, must less than 300BK
      if (fileSize > 300) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: '',
              content: errMsg,
            },
          ]),
        );
        return;
      }

      try {
        //TODO: check the resolution, must greater than 100*100
        const dimensions = await getImageSizeFromUrl(URL.createObjectURL(file));
        if (!dimensions || dimensions.height < 100 || dimensions.width < 100) {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: '',
                content: Translation('global.max.file.resolution.allow'),
              },
            ]),
          );
          return;
        }
      } catch (err) {
        console.error(err);
      }

      try {
        const createBlobRes = await createBlob(
          { mimeType: file.type, accessLevel: 'public', module: 'pruwallet' },
          dispatch,
        );
        await fileUpload(createBlobRes.url, file, setUploadThumbnailProgress);
        const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
        const result = blobDetail[0];
        if (result) {
          onDismissErrorHandler('thumbnailFileName', true);
          formDispatch({
            type: 'UPLOAD_THUMBNAIL_IMAGE',
            payload: {
              blobId: result.blobId,
              name: file.name,
              url: result.url,
            },
          });
        }
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('walletAward.success.uploadThumbnail', {
                name: file.name,
              }),
            },
          ]),
        );
      } catch (err) {
        console.error(err);
      } finally {
        setUploadThumbnailProgress(undefined);
      }
    }
  };

  // eslint-disable-next-line
  const removeThumbnailFile = () => {
    formDispatch({
      type: 'UPLOAD_THUMBNAIL_IMAGE',
      payload: {
        blobId: '',
        name: '',
        url: '',
      },
    });
    dispatch(
      appendAlertItem([
        {
          severity: AlertType.SUCCESS,
          title: 'Success',
          content: `Remove thumbnail file successfully`,
        },
      ]),
    );
  };

  // eslint-disable-next-line
  const uploadPdfFile = async ({
    file,
    setLoadPercentage,
  }: {
    file: any;
    fileName: string;
    fileType: string;
    description?: string;
    setLoadPercentage: (p: number) => void;
  }) => {
    const createBlobRes = await createBlob(
      { mimeType: file.type, accessLevel: 'public', module: 'pruwallet' },
      dispatch,
    );
    await fileUpload(createBlobRes.url, file, setLoadPercentage);
    const blobDetail = await getBlob({ resourceIds: createBlobRes.blobId }, dispatch);
    const result = blobDetail[0];
    if (result) {
      return result;
    } else {
      throw new Error('upload failed');
    }
  };
  const modifyTargetApplicant = (
    option: keyof Pick<
      WalletAwardFormState,
      'enrolmentType' | 'eligibleDesignationsHK' | 'eligibleDesignationsMO' | 'agentListUploaded'
    >,
    value: any,
  ) => {
    formDispatch({ type: 'MODIFY_FIELD', payload: { field: option, value } });
  };

  const onSubmitForm = async (status: AwardStatusEnum, actionKey: string) => {
    const { hasError } = onSubmitErrorValidator();
    if (hasError) return;
    // Call Api and submit
    try {
      setSubmittingAction(actionKey);
      setIsSubmitting(true);
      const nextFormState = { ...formState, status };
      const details = getSubmitDetail(nextFormState);
      if (!details) return;

      if (formMode === WalletAwardFormMode.CREATE) {
        await createWalletAward(details, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('walletAward.success.saved'),
            },
          ]),
        );
      } else if (formMode === WalletAwardFormMode.EDIT && code) {
        await modifyWalletAward(code, details, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: Translation('walletAward.success.saved'),
            },
          ]),
        );
      }
      onRouteTo(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
      setSubmittingAction('');
    }
  };

  const getTitle = () => {
    switch (formMode) {
      case WalletAwardFormMode.VIEW:
        return Translation('walletAward.view');
      case WalletAwardFormMode.CREATE:
        return Translation('walletAward.create');
      case WalletAwardFormMode.EDIT:
        return Translation('walletAward.edit');
      default:
        return Translation('walletAward.view');
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>{getTitle()}</div>
        </div>
        <Button
          variant="contained"
          color="inherit"
          onClick={() => {
            history.push(`${WALLET_BASE_PATH}${WALLET_AWARD_PATH}`);
          }}
        >
          {Translation('app.button.back')}
        </Button>
      </div>
      <div className={classes.sectionMargin}>
        <div className={classes.divideMargin}></div>

        {/* Last Updated Date */}
        {isEdit && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>{Translation('walletAward.common.lastUpdatedDate')} :</span>
            </div>
            <div style={{ flexGrow: 1 }}>
              {detail?.updatedAt ? getLocalDateFormat(detail.updatedAt, 'DD/MM/YYYY HH:mm') : ''}
            </div>
          </div>
        )}

        {/* Created By */}
        {isEdit && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>{Translation('walletAward.common.createdBy')} :</span>
            </div>
            <div style={{ flexGrow: 1 }}>{detail?.createdBy ? detail?.createdBy : ''}</div>
          </div>
        )}

        {/* Last Edited By */}
        {isEdit && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>{Translation('walletAward.common.lastEditedBy')} :</span>
            </div>
            <div style={{ flexGrow: 1 }}>{detail?.updatedBy ? detail?.updatedBy : ''}</div>
          </div>
        )}

        {/* Award Id */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation(`walletAward.common.awardId`)}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              disabled={isDisabled || isEdit}
              error={errorState.mandatory[`awardId`]}
              margin="dense"
              variant="outlined"
              fullWidth
              helperText={errorState.mandatory[`awardId`] && Translation('field_mandatory_helper_label')}
              value={formState.awardId}
              onChange={(e) => {
                onDismissErrorHandler(`awardId`, e.target.value);
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'awardId', value: e.target.value },
                });
              }}
            />
          </div>
        </div>

        {/* Award Name (Chinese) */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation(`walletAward.common.awardNameZh`)}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              disabled={!isEditableField('awardNameZh')}
              error={errorState.mandatory[`awardNameZh`]}
              margin="dense"
              variant="outlined"
              fullWidth
              helperText={errorState.mandatory[`awardNameZh`] && Translation('field_mandatory_helper_label')}
              value={formState.awardNameZh}
              onChange={(e) => {
                onDismissErrorHandler(`awardNameZh`, e.target.value);
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'awardNameZh', value: e.target.value },
                });
              }}
            />
          </div>
        </div>

        {/* Award Name (English) */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation(`walletAward.common.awardNameEn`)}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              disabled={!isEditableField('awardNameEn')}
              error={errorState.mandatory[`awardNameEn`]}
              margin="dense"
              variant="outlined"
              fullWidth
              helperText={errorState.mandatory[`awardNameEn`] && Translation('field_mandatory_helper_label')}
              value={formState.awardNameEn}
              onChange={(e) => {
                onDismissErrorHandler(`awardNameEn`, e.target.value);
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'awardNameEn', value: e.target.value },
                });
              }}
            />
          </div>
        </div>

        {/* Award Type */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.awardType')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>

          <div style={{ width: '75%' }}>
            <FormControl error={errorState.mandatory.awardType}>
              <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
                {Object.values(AwardTypeEnum).map((type, index) => (
                  <FormControlLabel
                    disabled={!isEditableField('awardType')}
                    key={`awardType-${type}`}
                    value={type}
                    control={<Radio disabled={false} />}
                    checked={formState.awardType === type}
                    label={Translation(`walletAward.common.awardType.option.${type}`)}
                    onChange={() => {
                      onDismissErrorHandler(`awardType`, type);
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: { field: 'awardType', value: type },
                      });

                      if (type === AwardTypeEnum.PROMO_CODE) {
                        if (!formState.promoCodeUsageLimit) {
                          formDispatch({
                            type: 'MODIFY_FIELD',
                            payload: { field: 'promoCodeUsageLimit', value: 1 },
                          });
                        }
                      } else {
                        if (formState.creditDeductedAtSubmission) {
                          formDispatch({
                            type: 'MODIFY_FIELD',
                            payload: { field: 'creditDeductedAtSubmission', value: false },
                          });
                        }
                      }
                    }}
                  />
                ))}
              </RadioGroup>
              {errorState.mandatory.awardType && <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>}
            </FormControl>
          </div>
        </div>

        {/* Credit deducted at proposal submission */}
        {formState.awardType === AwardTypeEnum.PROMO_CODE && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('walletAward.common.creditDeductedAtSubmission')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <div style={{ width: '75%' }}>
              <FormControl error={errorState.mandatory.creditDeductedAtSubmission}>
                <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
                  {[true, false].map((type, index) => (
                    <FormControlLabel
                      disabled={!isEditableField('creditDeductedAtSubmission')}
                      key={`creditDeductedAtSubmission-${type}`}
                      value={type}
                      control={<Radio disabled={false} />}
                      checked={formState.creditDeductedAtSubmission === type}
                      label={Translation(`walletAward.common.option.${type}`)}
                      onChange={() => {
                        formDispatch({
                          type: 'MODIFY_FIELD',
                          payload: { field: 'creditDeductedAtSubmission', value: type },
                        });
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        )}

        {/* Credit deducted ratio */}
        {formState.creditDeductedAtSubmission && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('walletAward.common.creditDeductionValue')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <FormControl error={errorState.mandatory.creditDeductionValue}>
              <TextField
                disabled={!isEditableField('creditDeductionValue')}
                style={{ width: 100 }}
                className={classes.inputMargin}
                margin="dense"
                variant="outlined"
                value={formState.creditDeductionValue}
                onChange={(e) => {
                  const value = e.target.value;
                  const regex = /^\d+(\.\d{0,4})?$/;
                  if (value) {
                    if (regex.test(value)) {
                      onDismissErrorHandler(`creditDeductionValue`, value);
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: { field: 'creditDeductionValue', value },
                      });
                    }
                  } else {
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'creditDeductionValue', value: '' },
                    });
                  }
                }}
              />
              {errorState.mandatory.creditDeductionValue && (
                <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}

        {/* Thumbnail */}
        <div className={classes.rowContainer} style={{ marginTop: 15 }}>
          <div className={classes.fieldContainer} style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
            <div>
              <span className={classes.field}>
                {Translation('walletAward.common.thumbnail')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
          </div>
          <div style={{ flexGrow: 1 }}>
            <input
              id="upload-thumbnail"
              hidden
              type="file"
              accept="image/jpg, image/jpeg, image/png"
              onChange={handleThumbnailFile}
            />
            <div className={classes.rowContainer}>
              <Button
                disabled={!isEditableField('thumbnail')}
                style={{ border: errorState.immediate.thumbnailFileName ? `1px solid #f018a6` : `0px` }}
                variant="contained"
                color="secondary"
                onClick={() => document.getElementById('upload-thumbnail')!.click()}
              >
                {Translation('newsArticle.common.chooseFile')}
              </Button>
              {formState.thumbnail?.url ? (
                <Button
                  style={{ marginLeft: 20 }}
                  disabled={!isEditableField('thumbnail')}
                  variant="contained"
                  color="secondary"
                  onClick={() => removeThumbnailFile()}
                >
                  {Translation('newsArticle.common.removeFile')}
                </Button>
              ) : null}
            </div>
            {errorState.mandatory.thumbnailFileName && (
              <span className={classes.errorText}>{MANDATORY_FIELD_ERROR_TEXT}</span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div>{Translation('walletAward.common.thumbnailHint')}</div>
            </div>
            {!!uploadThumbnailProgress && (
              <LinearProgress style={{ marginTop: 10 }} variant="determinate" value={uploadThumbnailProgress} />
            )}
          </div>
        </div>

        <div className={classes.rowContainer} style={{ marginTop: 15 }}>
          <div className={classes.fieldContainer}></div>
          <div style={{ flexGrow: 1 }}>
            <div className={classes.rowContainer}>
              <div>{formState.thumbnail?.name ? formState.thumbnail?.name : ''}</div>
            </div>
            <div className={classes.rowContainer}>
              {formState.thumbnail?.url && (
                <div>
                  <img
                    style={{ width: 'auto', height: '80px', marginBottom: '20px' }}
                    src={formState.thumbnail?.url}
                    alt="Thumbnail"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Award Details (Chinese) */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.awardDetailsZh')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ width: '75%' }}>
            <Tinymce
              value={formState.awardDetailsZh}
              disabled={!isEditableField('awardDetailsZh')}
              onChange={(value) => {
                onDismissErrorHandler(`awardDetailsZh`, value);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardDetailsZh', value } });
              }}
              onChangeWordCount={(value) => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardDetailsZhWordCount', value } });
              }}
              module="pruwallet"
            />
            {errorState.mandatory.awardDetailsZh && (
              <span className={classes.errorText}>{MANDATORY_FIELD_ERROR_TEXT}</span>
            )}
          </div>
        </div>

        {/* Award Details (English) */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.awardDetailsEn')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ width: '75%' }}>
            <Tinymce
              value={formState.awardDetailsEn}
              disabled={!isEditableField('awardDetailsEn')}
              onChange={(value) => {
                onDismissErrorHandler(`awardDetailsEn`, value);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardDetailsEn', value } });
              }}
              onChangeWordCount={(value) => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardDetailsEnWordCount', value } });
              }}
              module="pruwallet"
            />
            {errorState.mandatory.awardDetailsEn && (
              <span className={classes.errorText}>{MANDATORY_FIELD_ERROR_TEXT}</span>
            )}
          </div>
        </div>

        {/* Related Flyers */}
        <div style={{ height: 10 }} />
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation('walletAward.common.relatedFlyers')} :</span>
          </div>
          <div style={{ width: '75%' }}>
            <MultiFileUploader
              disabled={!isEditableField('relatedFlyers')}
              upload={uploadPdfFile}
              download={() => {}}
              values={formState.relatedFlyers}
              allowedFileTypes={['pdf']}
              onChange={(value) => {
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'relatedFlyers', value } });
              }}
            />
          </div>
        </div>

        {/* Require Redemption */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.requireRedemption')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ width: '75%' }}>
            <FormControl error={errorState.mandatory.requireRedemption}>
              <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
                {[true, false].map((type, index) => (
                  <FormControlLabel
                    disabled={!isEditableField('requireRedemption')}
                    key={`requireRedemption-${type}`}
                    value={type}
                    control={<Radio disabled={false} />}
                    checked={formState.requireRedemption === type}
                    label={Translation(`walletAward.common.option.${type}`)}
                    onChange={() => {
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: { field: 'requireRedemption', value: type },
                      });
                    }}
                  />
                ))}
              </RadioGroup>
              {errorState.mandatory.requireRedemption && <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>}
            </FormControl>
          </div>
        </div>

        {formState.requireRedemption && (
          <>
            {/* Send notification */}
            <div className={classes.rowContainer}>
              <div className={classes.fieldContainer}>
                <span className={classes.field}>
                  {Translation('walletAward.common.sendNotification')}
                  <span className={classes.mandatory}>*</span> :
                </span>
              </div>
              <div style={{ width: '75%' }}>
                <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
                  {[true, false].map((value, index) => (
                    <FormControlLabel
                      value={value}
                      disabled={!isEditableField('sendNotification')}
                      control={<Radio disabled={false} />}
                      checked={formState.sendNotification === value}
                      label={Translation(`walletAward.common.option.${value}`)}
                      onChange={() => {
                        formDispatch({
                          type: 'MODIFY_FIELD',
                          payload: { field: 'sendNotification', value },
                        });
                      }}
                    />
                  ))}
                </RadioGroup>
              </div>
            </div>
            {/* Min. credit for redemption */}
            <div className={classes.rowContainer}>
              <div className={classes.fieldContainer}>
                <span className={classes.field}>
                  {Translation('walletAward.common.minCreditForRedemption')}
                  <span className={classes.mandatory}>*</span>&nbsp;:
                </span>
              </div>
              <FormControl error={errorState.mandatory.minCreditForRedemption}>
                <TextField
                  disabled={!isEditableField('minCreditForRedemption')}
                  style={{ width: 100 }}
                  className={classes.inputMargin}
                  margin="dense"
                  variant="outlined"
                  value={formState.minCreditForRedemption}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    onDismissErrorHandler(`minCreditForRedemption`, value);
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'minCreditForRedemption', value },
                    });
                  }}
                />
                {errorState.mandatory.minCreditForRedemption && (
                  <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                )}
              </FormControl>
            </div>
          </>
        )}

        {/* Credit deducted at redemption */}
        {formState.requireRedemption && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('walletAward.common.creditDeductedAtRedemption')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <FormControl error={errorState.mandatory.creditDeductedAtRedemption}>
              <TextField
                disabled={!isEditableField('creditDeductedAtRedemption')}
                style={{ width: 100 }}
                className={classes.inputMargin}
                margin="dense"
                variant="outlined"
                value={formState.creditDeductedAtRedemption}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  onDismissErrorHandler(`creditDeductedAtRedemption`, value);
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'creditDeductedAtRedemption', value },
                  });
                }}
              />
              {errorState.mandatory.creditDeductedAtRedemption && (
                <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}

        {/* Negative balance set up */}
        {formState.awardType === AwardTypeEnum.PROMO_CODE &&
          formState.creditDeductedAtSubmission &&
          formState.requireRedemption && (
            <>
              {/* Allow Negative Balance */}
              <div className={classes.rowContainer}>
                <div className={classes.fieldContainer}>
                  <span className={classes.field}>
                    {Translation('walletAward.common.allowNegativeBalance')}
                    <span className={classes.mandatory}>*</span> :
                  </span>
                </div>
                <div style={{ width: '75%' }}>
                  <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
                    {[true, false].map((value, index) => (
                      <FormControlLabel
                        value={value}
                        disabled={!isEditableField('allowNegativeBalance')}
                        control={<Radio disabled={false} />}
                        checked={formState.allowNegativeBalance === value}
                        label={Translation(`walletAward.common.option.${value}`)}
                        onChange={() => {
                          formDispatch({
                            type: 'MODIFY_FIELD',
                            payload: { field: 'allowNegativeBalance', value },
                          });
                        }}
                      />
                    ))}
                  </RadioGroup>
                </div>
              </div>
              {/* Maximium Negative Cap */}
              {formState.allowNegativeBalance && (
                <div className={classes.rowContainer}>
                  <div className={classes.fieldContainer}>
                    <span className={classes.field}>
                      {Translation('walletAward.common.maximumNegativeCap')}
                      <span className={classes.mandatory}>*</span> :
                    </span>
                  </div>
                  <FormControl error={errorState.mandatory.maximumNegativeCap}>
                    <TextField
                      disabled={!isEditableField('maximumNegativeCap')}
                      style={{ width: 100 }}
                      className={classes.inputMargin}
                      margin="dense"
                      variant="outlined"
                      value={formState.maximumNegativeCap}
                      onChange={(e) => {
                        const value = e.target.value;
                        formDispatch({
                          type: 'MODIFY_FIELD',
                          payload: { field: 'maximumNegativeCap', value },
                        });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (!isStrictlyNegative(value)) {
                          formDispatch({
                            type: 'MODIFY_FIELD',
                            payload: { field: 'maximumNegativeCap', value },
                          });
                        }
                      }}
                    />
                    {errorState.mandatory.maximumNegativeCap && (
                      <FormHelperText>{Translation('walletAward.common.negativeNumberOnly')}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            </>
          )}
        {/* Allow Redemption Start Date */}
        {/* Allow Redemption End Date */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation('walletAward.common.redemptionDate')} :</span>
          </div>
          <PruDatePicker
            disabled={!isEditableField('redemptionDate')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                error: errorState.mandatory.redemptionStartDate,
                helperText: errorState.mandatory.redemptionStartDate && MANDATORY_FIELD_ERROR_TEXT,
              },
            }}
            maxDate={formState.redemptionEndDate}
            value={formState.redemptionStartDate}
            onChange={(date) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'redemptionStartDate', value: date } });
            }}
          />
          <div className={classes.dateDivider}>-</div>
          <PruDatePicker
            disabled={!isEditableField('redemptionDate')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                error: errorState.mandatory.redemptionEndDate,
                helperText: errorState.mandatory.redemptionEndDate && MANDATORY_FIELD_ERROR_TEXT,
              },
            }}
            minDate={formState.redemptionStartDate}
            value={formState.redemptionEndDate}
            onChange={(date) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'redemptionEndDate', value: date } });
            }}
          />
        </div>

        {/* Award Validity Start Date */}
        {/* Award Validity End Date */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.awardValidityDate')} <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <PruDatePicker
            disabled={!isEditableField('awardValidityDate')}
            slotProps={{
              textField: {
                error: errorState.mandatory.awardValidityStartDate,
                helperText: errorState.mandatory.awardValidityStartDate && MANDATORY_FIELD_ERROR_TEXT,
              },
            }}
            format="DD/MM/YYYY"
            maxDate={formState.awardValidityEndDate}
            value={formState.awardValidityStartDate}
            onChange={(date) => {
              onDismissErrorHandler('awardValidityStartDate', date);
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardValidityStartDate', value: date } });
            }}
          />
          <div className={classes.dateDivider}>-</div>
          <PruDatePicker
            disabled={!isEditableField('awardValidityDate')}
            slotProps={{
              textField: {
                error: errorState.mandatory.awardValidityEndDate,
                helperText: errorState.mandatory.awardValidityEndDate && MANDATORY_FIELD_ERROR_TEXT,
              },
            }}
            format="DD/MM/YYYY"
            minDate={formState.awardValidityStartDate}
            value={formState.awardValidityEndDate}
            onChange={(date) => {
              onDismissErrorHandler('awardValidityEndDate', date);
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'awardValidityEndDate', value: date } });
            }}
          />
        </div>

        {/* Promo Code Usage Limit */}
        {formState.awardType === AwardTypeEnum.PROMO_CODE && (
          <div className={classes.rowContainer}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {Translation('walletAward.common.promoCodeUsageLimit')}
                <span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <FormControl error={errorState.mandatory.promoCodeUsageLimit}>
              <TextField
                disabled={!isEditableField('promoCodeUsageLimit')}
                style={{ width: 100 }}
                className={classes.inputMargin}
                margin="dense"
                variant="outlined"
                value={formState.promoCodeUsageLimit}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  formDispatch({
                    type: 'MODIFY_FIELD',
                    payload: { field: 'promoCodeUsageLimit', value },
                  });
                }}
              />
              {errorState.mandatory.promoCodeUsageLimit && (
                <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}

        {/* Max. no of redemption by each agent */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>{Translation('walletAward.common.maxRedemptionPerUser')} :</span>
          </div>
          <TextField
            disabled={!isEditableField('maxRedemptionPerUser')}
            style={{ width: 100 }}
            className={classes.inputMargin}
            margin="dense"
            variant="outlined"
            value={formState.maxRedemptionPerUser}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              formDispatch({
                type: 'MODIFY_FIELD',
                payload: { field: 'maxRedemptionPerUser', value },
              });
            }}
          />
        </div>

        {/* Display Remaining Quota */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('walletAward.common.displayRemainingQuota')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ width: '75%' }}>
            <RadioGroup style={{ display: 'flex', flexDirection: 'row' }}>
              {[true, false].map((type, index) => (
                <FormControlLabel
                  key={`displayRemainingQuota-${type}`}
                  disabled={!isEditableField('displayRemainingQuota')}
                  value={type}
                  control={<Radio disabled={false} />}
                  checked={formState.displayRemainingQuota === type}
                  label={Translation(`walletAward.common.option.${type}`)}
                  onChange={() => {
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'displayRemainingQuota', value: type },
                    });
                  }}
                />
              ))}
            </RadioGroup>
          </div>
        </div>

        {/* Eligible Agents */}
        <div className={`${classes.rowContainer} tw-mt-4`}>
          <div className={classes.fieldContainer} style={{ alignSelf: 'flex-start', paddingTop: '8px' }}>
            <span className={classes.field}>
              {Translation('walletAward.common.targetApplicants')}
              {/* <span className={classes.mandatory}>*</span> : */}
            </span>
          </div>
          <div style={{ width: '75%' }}>
            <TargetApplicantComponent
              modifyTargetApplicant={modifyTargetApplicant}
              enrolmentType={formState.enrolmentType}
              formState={formState}
              formMode={formMode}
              formDispatch={formDispatch}
              disabled={!isEditableField('targetApplicants')}
            />
            {errorState.mandatory.designation && (
              <span className={classes.errorText}>{Translation('field_mandatory_helper_label')}</span>
            )}
          </div>
        </div>
      </div>

      <div className={classes.footerContainer}>
        <>
          {/* Create mode */}
          {!isDisabled && !isEdit && (
            <>
              <Button
                disabled={isSubmitting && submittingAction === 'create-draft'}
                variant="contained"
                color="secondary"
                onClick={() => onSubmitForm(AwardStatusEnum.DRAFT, 'create-draft')}
              >
                {Translation('app.button.saveAsDraft')}
                {isSubmitting && submittingAction === 'create-draft' && (
                  <CircularProgress style={{ marginLeft: 8 }} size={15} />
                )}
              </Button>
              <Button
                disabled={isSubmitting && submittingAction === 'create-pending'}
                style={{ marginLeft: 25 }}
                variant="contained"
                color="secondary"
                onClick={() => onSubmitForm(AwardStatusEnum.PENDING, 'create-pending')}
              >
                {Translation('app.button.submitToApprove')}
                {isSubmitting && submittingAction === 'create-pending' && (
                  <CircularProgress style={{ marginLeft: 8 }} size={15} />
                )}
              </Button>
              {approvePermission && (
                <Button
                  disabled={isSubmitting && submittingAction === 'create-active'}
                  style={{ marginLeft: 25 }}
                  variant="contained"
                  color="secondary"
                  onClick={() => onSubmitForm(AwardStatusEnum.ACTIVE, 'create-active')}
                >
                  {Translation('app.button.approveAndActivate')}
                  {isSubmitting && submittingAction === 'create-active' && (
                    <CircularProgress style={{ marginLeft: 8 }} size={15} />
                  )}
                </Button>
              )}
            </>
          )}

          {/* Edit mode */}
          <>
            {detail?.status === AwardStatusEnum.DRAFT && (
              <>
                <Button
                  disabled={isSubmitting && submittingAction === 'edit-draft'}
                  variant="contained"
                  color="secondary"
                  onClick={() => onSubmitForm(AwardStatusEnum.DRAFT, 'edit-draft')}
                >
                  {Translation('app.button.saveAsDraft')}
                  {isSubmitting && submittingAction === 'edit-draft' && (
                    <CircularProgress style={{ marginLeft: 8 }} size={15} />
                  )}
                </Button>
                <Button
                  disabled={isSubmitting && submittingAction === 'edit-pending'}
                  style={{ marginLeft: 25 }}
                  variant="contained"
                  color="secondary"
                  onClick={() => onSubmitForm(AwardStatusEnum.PENDING, 'edit-pending')}
                >
                  {Translation('app.button.submitToApprove')}
                  {isSubmitting && submittingAction === 'edit-pending' && (
                    <CircularProgress style={{ marginLeft: 8 }} size={15} />
                  )}
                </Button>
              </>
            )}

            {approvePermission && (
              <>
                {(detail?.status === AwardStatusEnum.DRAFT ||
                  detail?.status === AwardStatusEnum.PENDING ||
                  detail?.status === AwardStatusEnum.ACTIVE ||
                  detail?.status === AwardStatusEnum.PAUSED) && (
                  <Button
                    disabled={isSubmitting && submittingAction === 'edit-active'}
                    style={{ marginLeft: 25 }}
                    variant="contained"
                    color="secondary"
                    onClick={() => onSubmitForm(AwardStatusEnum.ACTIVE, 'edit-active')}
                  >
                    {Translation('walletAward.common.button.approveAndActive')}
                    {isSubmitting && submittingAction === 'edit-active' && (
                      <CircularProgress style={{ marginLeft: 8 }} size={15} />
                    )}
                  </Button>
                )}

                {detail?.status === AwardStatusEnum.PENDING && (
                  <Button
                    disabled={isSubmitting && submittingAction === 'edit-reject'}
                    style={{ marginLeft: 25 }}
                    variant="contained"
                    color="secondary"
                    onClick={() => onSubmitForm(AwardStatusEnum.DRAFT, 'edit-reject')}
                  >
                    {Translation('walletAward.common.status.option.reject')}
                    {isSubmitting && submittingAction === 'edit-reject' && (
                      <CircularProgress style={{ marginLeft: 8 }} size={15} />
                    )}
                  </Button>
                )}
              </>
            )}
          </>
        </>
      </div>
    </div>
  );
};

export default AwardDetail;
